import React, { forwardRef, useCallback, useRef } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import map from 'lodash/map'
import isEmpty from 'lodash/isEmpty'
import type { Notification } from '@ztechapps/common.contract'
import type { UseListResult } from '@ztechapps/common.hook'
import { Spinner, HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { BusyButtonLink } from '@ztechapps/webui.button'

import { NotificationBell } from '../NotificationBell'
import { NotificationDropdownItem } from './NotificationDropdownItem'
import { NotificationDropdownEmpty } from './NotificationDropdownEmpty'
import { NotificationDropdownDesktopAction } from './NotificationDropdownDesktopAction'
import styles from './NotificationDropdown.module.scss'

export type NotificationDropdownProps = {
  notifications: UseListResult<Notification>
  loading?: boolean
  onToggleRead: (notification: Notification) => Promise<void>
  onReadAll?: () => Promise<void>
  notificationListRoute: string
}

type CustomToggleProps = {
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void
}

// Custom toggle following React Bootstrap documentation pattern
// Uses button element for proper keyboard accessibility
const CustomToggle = forwardRef<HTMLDivElement, CustomToggleProps>(({ onClick }, ref) => (
  <div
    ref={ref}
    onClick={(e) => {
      onClick(e)
    }}
  >
    <NotificationBell />
  </div>
))

export const NotificationDropdown = ({
  notifications,
  loading,
  onToggleRead,
  onReadAll,
  notificationListRoute,
}: NotificationDropdownProps) => {
  const navigate = useNavigate()
  const notificationList = notifications?.list ?? []
  const hasNotifications = !isEmpty(notificationList)
  const loadingMore = useRef(false)

  const pageCount = notifications?.pageInfo?.count ?? 0
  const listCount = notificationList.length
  const hasMore = listCount > 0 && pageCount > 0 && pageCount > listCount

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget
      const scrolledToBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50

      if (scrolledToBottom && hasMore && !notifications?.loading && !loadingMore.current) {
        loadingMore.current = true
        notifications?.loadMoreEntries?.(listCount, '', notifications?.sort)
        setTimeout(() => {
          loadingMore.current = false
        }, 500)
      }
    },
    [hasMore, notifications, listCount]
  )

  const handleReadAll = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    await onReadAll?.()
  }

  const handleViewAll = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(notificationListRoute)
  }

  return (
    <Dropdown align="end">
      <Dropdown.Toggle as={CustomToggle} id="notification-dropdown-toggle" />
      <Dropdown.Menu className={styles.dropdownMenu}>
        {/* Fixed Header */}
        <div className={styles.fixedHeader}>
          <HorizontalStack className="justify-content-between align-items-center px-3 py-2">
            <span className="fw-bold">Notifications</span>
            <HorizontalStack gap={2} className="align-items-center">
              <ShowIf condition={!!loading}>
                <Spinner size="sm" />
              </ShowIf>
              <BusyButtonLink onClick={handleViewAll} text="View All" />
              <div>|</div>
              <ShowIf condition={!!onReadAll}>
                <BusyButtonLink onClick={handleReadAll} text="Read All" />
              </ShowIf>
            </HorizontalStack>
          </HorizontalStack>
          <Dropdown.Divider className="m-0" />
          <NotificationDropdownDesktopAction />
        </div>

        {/* Scrollable Content */}
        <div className={styles.scrollableContent} onScroll={handleScroll}>
          <ShowIf condition={!hasNotifications}>
            <NotificationDropdownEmpty />
          </ShowIf>

          <ShowIf condition={hasNotifications}>
            {map(notificationList, (notification) => (
              <NotificationDropdownItem
                key={notification._id}
                notification={notification}
                loading={loading}
                onToggleRead={onToggleRead}
              />
            ))}
          </ShowIf>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )
}

export { NotificationDropdownItem } from './NotificationDropdownItem'
export { NotificationDropdownEmpty } from './NotificationDropdownEmpty'
